import { useCallback, useEffect, useState } from 'react';
import { Icon, Grid } from 'semantic-ui-react';

import { POSButton, POSLoadingIndicator, useEnvironment } from '@publicisgroupe/p-os-components';

import { AppSettings } from 'types/appSettings';
import { CookieBannerActions } from 'utils/enums';
import { handleCookieBannerActions } from 'components/CookieBannerModal/CookieBannerHelper';
import { t } from 'utils/resources';
import { useCookieData } from './hooks/useCookieData';
import { useCookieDeclarationData } from './hooks/useCookieDeclarationData';
import { CookieBannerDto, CookieDeclarationDto } from './types';

import './CookieBanner.less';
import useErrorNotification from 'hooks/useErrorNotification';

interface CookieBannerProps {
  closeBanner: () => void;
}

export const CookieBanner = ({ closeBanner }: CookieBannerProps) => {
  const { settings } = useEnvironment();
  const cookieDataRequest = useCookieData();
  const cookieDeclarationDataRequest = useCookieDeclarationData();
  const { showErrorNotification } = useErrorNotification();

  const [showMoreText, setShowMoreText] = useState<boolean>(false);
  const [cookieData, setCookieData] = useState<CookieBannerDto>();
  const [cookieDeclarationData, setCookieDeclarationData] = useState<CookieDeclarationDto[]>([]);

  const appSettings = settings as AppSettings;
  const { UseConfigModule: useConfigModule } = appSettings;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cookieData = await cookieDataRequest(useConfigModule);
        const cookieDeclarationData = await cookieDeclarationDataRequest(useConfigModule);

        setCookieData(cookieData);
        setCookieDeclarationData(cookieDeclarationData);
      } catch (error) {
        showErrorNotification({
          errorTitle: `${t('CookieLoadErrorTitle')}`,
          content: t('CookieLoadErrorMessage'),
        });
      }
    };

    fetchData();
  }, [cookieDataRequest, cookieDeclarationDataRequest, useConfigModule, showErrorNotification]);

  const toggleShowMoreText = useCallback(() => {
    setShowMoreText(!showMoreText);
  }, [showMoreText]);

  const handleAcceptCookies = useCallback(() => {
    handleCookieBannerActions(CookieBannerActions.Accept, cookieDeclarationData);
    closeBanner();
  }, [closeBanner, cookieDeclarationData]);

  const handleDeclineCookies = useCallback(() => {
    handleCookieBannerActions(CookieBannerActions.Decline, cookieDeclarationData);
    closeBanner();
  }, [closeBanner, cookieDeclarationData]);

  if (!cookieData || !cookieDeclarationData.length)
    return <POSLoadingIndicator text={t('Loading')} />;

  return (
    <>
      <div className="cookie-banner-title">
        <Icon className={`pos ${cookieData?.icon}`} size="big" />
        <h1>{cookieData?.title}</h1>
      </div>

      <div className="cookie-banner-text">
        <div
          className="text"
          dangerouslySetInnerHTML={{
            __html: showMoreText ? cookieData?.longText ?? '' : cookieData?.shortText ?? '',
          }}
        />
      </div>
      <Grid className="cookie-banner-action-btns">
        <Grid.Row>
          <Grid.Column width={4}>
            <POSButton inverted onClick={handleDeclineCookies}>
              {t('CookieBanner.Decline')}
            </POSButton>
          </Grid.Column>
          <Grid.Column width={12}>
            <POSButton floated="right" onClick={handleAcceptCookies}>
              {t('CookieBanner.Accept')}
            </POSButton>
            <POSButton floated="right" inverted onClick={toggleShowMoreText}>
              {showMoreText ? t('CookieBanner.Back') : t('CookieBanner.More')}
            </POSButton>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};
